import React, { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, IconButton, Icon, Slide } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import axios from "axios";
import { FONT_SIZE, RADIUS, BRAND, MODAL_WIDTH_MD } from "../theme/uiTokens";

// Absolute URLs
const START_API_URL = "https://camera1.dhundhoo.com/internal/camera/start";
const STOP_API_URL = "https://camera1.dhundhoo.com/internal/camera/stop";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

function LiveCameraModal({ open, onClose, vehicleId, imei, sessionToken }) {
  const [activeCameras, setActiveCameras] = useState([]);
  const [loadingStream, setLoadingStream] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  
  // State to handle layout switch (vertical list vs grid)
  const [isGridView, setIsGridView] = useState(false); 
  
  const isStreamActiveRef = useRef(false);

  // 👇 STOP STREAM LOGIC (Fully Optimized for Background/Close)
  const executeStopStream = useCallback((isUnload = false) => {
    if (isStreamActiveRef.current && imei) {
      console.log(`🛑 Stopping Stream API | Mode: ${isUnload ? "Background/Hidden/Close" : "Axios Manual"} | IMEI: ${imei}`);

      const payloadString = JSON.stringify({ sim: imei });
      const headers = {
        // "x-dhundhoo-session": sessionToken || "",
        "Content-Type": "application/json",
      };

      // Set to false immediately to prevent duplicate calls
      isStreamActiveRef.current = false;

      if (isUnload) {
        // 1. First try sendBeacon (Best for unloads and mobile app close)
        const blob = new Blob([payloadString], { type: 'application/json' });
        const beaconSent = navigator.sendBeacon(STOP_API_URL, blob);

        // 2. Fallback to fetch keepalive if sendBeacon fails or isn't supported
        if (!beaconSent) {
          try {
            fetch(STOP_API_URL, {
              method: "POST",
              headers,
              body: payloadString,
              keepalive: true,
            }).catch((err) => console.error("Fetch stop failed:", err));
          } catch (e) {}
        }
      } else {
        // Manual Modal Close via Axios (Visible in Network tab)
        axios.post(STOP_API_URL, { sim: imei }, { headers })
          .then(() => console.log("✅ Stop API Success (Manual)"))
          .catch((err) => console.error("❌ Stop API Failed:", err));
      }
    }
  }, [imei, sessionToken]);

  // 👇 START STREAM LOGIC
  const startCameraStream = useCallback(async (simImei) => {
    if (isStreamActiveRef.current) return; // Prevent double start

    setLoadingStream(true);
    setErrorMsg("");
    setActiveCameras([]);

    try {
      console.log("▶️ Starting Stream API Called for IMEI:", simImei);
      const res = await axios.post(
        START_API_URL,
        { sim: simImei },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = res.data;
      
      if (data && data.streams && Array.isArray(data.streams)) {
        const streamsWithAutoplay = data.streams.map((stream) => {
          let videoUrl = stream.embedUrl;
          if (videoUrl && !videoUrl.includes("autoplay=true")) {
            videoUrl = `${videoUrl}${videoUrl.includes("?") ? "&" : "?"}autoplay=true&mute=true`;
          }
          return {
            ...stream,
            embedUrl: videoUrl
          };
        });

        isStreamActiveRef.current = true;
        setActiveCameras(streamsWithAutoplay);
      } else {
        setErrorMsg("No streams found for this vehicle.");
      }

    } catch (err) {
      console.error("Stream start error:", err);
      setErrorMsg("Failed to start camera stream. Please try again.");
    } finally {
      setLoadingStream(false);
    }
  }, []);

  // 👇 MOUNT / UNMOUNT & BACKGROUND EVENTS LISTENER
  useEffect(() => {
    const handleUnload = () => executeStopStream(true);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // Tab changed or browser minimized -> STOP stream to save bandwidth/server load
        executeStopStream(true);
      } else if (document.visibilityState === "visible" && open && imei) {
        // User came back to the tab -> RESTART stream automatically
        startCameraStream(imei);
      }
    };

    // Attach all bulletproof listeners
    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("unload", handleUnload);
    window.addEventListener("pagehide", handleUnload); // For iOS Safari / Mobile
    document.addEventListener("visibilitychange", handleVisibilityChange); // For Tab Switch

    return () => {
      // Cleanup all listeners
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("unload", handleUnload);
      window.removeEventListener("pagehide", handleUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      
      // Stop stream on component unmount
      executeStopStream(true);
    };
  }, [open, imei, executeStopStream, startCameraStream]);

  // Initial Open Trigger
  useEffect(() => {
    if (open && imei) {
      startCameraStream(imei);
    }
  }, [open, imei, startCameraStream]);

  const handleCloseStreamModal = () => {
    executeStopStream(false); // isUnload = false means Manual Close
    setActiveCameras([]);
    setIsGridView(false); 
    onClose();
  };

  const toggleLayout = () => {
    setIsGridView(!isGridView);
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseStreamModal}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
      keepMounted
      hideBackdrop={true}
      disableEnforceFocus
      style={{ pointerEvents: "none" }}
      sx={{
        "& .MuiDialog-container": {
          justifyContent: "flex-end", 
          alignItems: "flex-start",
        },
      }}
      PaperProps={{
        sx: {
          pointerEvents: "auto",
          m: 2,
          mt: 10,
          borderRadius: `${RADIUS.sm}px`,
          overflow: "hidden",
          backgroundColor: "#fff",
          width: {
            xs: MODAL_WIDTH_MD.xs,
            sm: isGridView && activeCameras.length > 1 ? 640 : 360,
            md: isGridView && activeCameras.length > 1 ? "800px" : "400px",
          },
          boxShadow: "0px 10px 30px rgba(0,0,0,0.5)",
          transition: "width 0.3s ease-in-out",
        },
      }}
    >
      {/* --- HEADER --- */}
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ 
            backgroundColor: BRAND.accent,
            color: "#fff", 
            px: 2, 
            py: 0.5,
            height: "45px" 
        }}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon fontSize="small">location_on</Icon>
          <MDTypography variant="subtitle2" color="white" sx={{ fontWeight: 400, fontSize: FONT_SIZE.subtitle }}>
            Map + Video
          </MDTypography>
        </MDBox>

        <MDBox display="flex" alignItems="center" gap={1}>
           {/* Grid Toggle Button */}
          <IconButton size="small" onClick={toggleLayout} sx={{ color: "#fff" }}>
            <Icon>{isGridView ? "view_agenda" : "grid_view"}</Icon> 
          </IconButton>
          <IconButton size="small" onClick={handleCloseStreamModal} sx={{ color: "#fff" }}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </MDBox>

      {/* --- MODAL BODY --- */}
      <DialogContent 
        sx={{ 
            p: 1.5, 
            minHeight: "400px", 
            maxHeight: "80vh", 
            overflowY: "auto",
            backgroundColor: "#f5f5f5" 
        }}
      >
        {loadingStream ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" height="200px">
            <MDTypography color="text">Starting camera feeds...</MDTypography>
          </MDBox>
        ) : errorMsg ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" height="200px">
            <MDTypography color="error" variant="caption">{errorMsg}</MDTypography>
          </MDBox>
        ) : activeCameras.length === 0 ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" height="200px">
            <MDTypography color="text">Waiting for streaming from device...</MDTypography>
          </MDBox>
        ) : (
          <MDBox 
            display="grid" 
            gridTemplateColumns={{ 
                xs: "1fr", 
                md: isGridView && activeCameras.length > 1 ? "1fr 1fr" : "1fr" 
            }} 
            gap={2}
          >
            {activeCameras.map((cam) => (
              <MDBox
                key={cam.streamId}
                sx={{
                  width: "100%",
                  background: "#000",
                  position: "relative",
                  border: "2px solid #fff",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                }}
              >
                {/* Overlay Text for each camera */}
                <MDBox p={1} position="absolute" top={0} left={0} zIndex={10} width="100%">
                  <MDTypography variant="caption" color="white" fontWeight="bold" sx={{ display: 'block', lineHeight: 1.3, fontSize: FONT_SIZE.small }}>
                    {vehicleId} | CH{cam.channel}
                    <span style={{ float: 'right', fontWeight: 'normal' }}>0 fps | 0 Bytes | 0 mb/s</span>
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ display: 'block', fontSize: FONT_SIZE.caption, mt: 0.5, lineHeight: 1.3 }}>
                     Loading Location...<br/>
                     <span style={{color: "#FFEB3B"}}>● Idle</span>
                  </MDTypography>
                </MDBox>

                <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, marginTop: "40px" }}>
                  <iframe
                    title={`stream-${cam.streamId}`}
                    src={cam.embedUrl}
                    frameBorder="0"
                    allow="autoplay; fullscreen"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </div>
              </MDBox>
            ))}
          </MDBox>
        )}
      </DialogContent>
    </Dialog>
  );
}

LiveCameraModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vehicleId: PropTypes.string,
  imei: PropTypes.string,
  sessionToken: PropTypes.string,
};

export default LiveCameraModal;